/**
 * AP-30-E — Rollen und Rechte.
 *
 * **Die Vorlagen sind nicht Komfort, sie sind der Grund, warum das Werkzeug
 * benutzt wird.** Ohne sie baut jeder Kunde seine Rollen aus einem Katalog von
 * achtzig Häkchen zusammen — dasselbe Verhältnis wie zwischen Modul und
 * Verkaufspaket in AP-29. Die Seite führt deshalb zuerst die fünf Systemrollen
 * und bietet daneben genau eine Handlung an: **kopieren**.
 *
 * Bewusst **keine** Listen-Standard-Seite: Das ist keine Auswertung, die man
 * filtert, sortiert und exportiert, sondern eine Handvoll Rollen mit einer
 * Häkchenliste. Dieselbe Einordnung wie `ModuleSeatsPage`.
 *
 * Zwei Dinge macht die Oberfläche sichtbar, die man sonst nur im Code sieht:
 *
 * - Eine **Systemrolle** ist unveränderlich. Sie verweist auf ihre Vorlage; die
 *   Häkchen sind zu sehen, aber nicht zu bedienen.
 * - Ein Recht, dessen **Modul fehlt**, steht ausgegraut da statt zu verschwinden
 *   — mit dem Modulnamen dabei. Verborgen wäre es die Frage „warum kann ich das
 *   nicht vergeben?", auf die es keine Antwort gibt (AP-29-N3).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Copy, Pencil, Plus, Save, ShieldCheck, Trash2, X } from 'lucide-react'
import {
  MODULE_REGISTRY,
  ROLE_TEMPLATES,
  isFieldPermission,
  modulesWithPermissions,
} from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { useTerminology } from '@/lib/useTerminology'
import { formCard, inputBase, pageTitle, pageWide, fieldLabel } from '@/lib/form-styles'
import { rolesApi, type RoleView } from '@/lib/roles/roles.api'
import { apiErrorText } from '@/lib/api-error'
import { usersApi } from '@/lib/users/users.api'
import type { User } from '@/lib/users/user.types'
import { useWarnungBeimVerlassen } from '@/lib/speichern'

export default function RolesPage() {
  const { t } = useTranslation()
  const terms = useTerminology()

  /**
   * Ein Rechtename in Klartext: `time.entry.read.own` → „Zeitbuchungen: lesen
   * (eigene)". Fehlt die Übersetzung, steht der Schlüssel da — lesbar genug, um
   * zu erkennen, was fehlt, und besser als eine leere Zeile.
   */
  // AP-35: Die Rechte des Projektmanagements tragen den Begriff des Mandanten.
  const rechtLabel = (permission: string) =>
    t(`permissions.${permission}`, { defaultValue: permission, projectPlural: terms.projectPlural })
  const { user, hasModule } = useAuthStore()

  const [rollen, setRollen] = useState<RoleView[]>([])
  /** Alle Benutzer des Mandanten — die Zuweisung ist eine Häkchenliste, keine Suche. */
  const [benutzer, setBenutzer] = useState<User[]>([])
  /** Welche Rolle zeigt gerade ihre Trägerliste? */
  const [offeneZuweisung, setOffeneZuweisung] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)

  /** Welche Rolle wird gerade bearbeitet? `'neu'` = eine neue eigene Rolle. */
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)
  const [entwurf, setEntwurf] = useState<{
    key: string
    name: string
    description: string
    permissions: Set<string>
  }>({ key: '', name: '', description: '', permissions: new Set() })
  const [speichert, setSpeichert] = useState(false)
  /** Stand des Entwurfs beim Öffnen — geändert ist, was davon abweicht. */
  const [ursprung, setUrsprung] = useState('')
  const alsText = (e: typeof entwurf) => JSON.stringify({ ...e, permissions: [...e.permissions].sort() })
  // AP-36 5.2: Ausnahme mit Knopf — ungespeichert warnt der Browser beim Schließen.
  useWarnungBeimVerlassen(bearbeitet !== null && alsText(entwurf) !== ursprung)

  const laden = useCallback(async () => {
    setLoading(true)
    setFehler(null)
    try {
      setRollen(await rolesApi.list())
    } catch (e: unknown) {
      setFehler(apiErrorText(e, t('roles.loadFailed', { defaultValue: 'Die Rollen konnten nicht geladen werden.' })))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { void laden() }, [laden])

  useEffect(() => {
    // Ein Betrieb hat Dutzende Mitarbeiter, keine Tausende — dieselbe
    // Einordnung wie bei der Platzvergabe (AP-29-H): ganze Liste, Häkchen,
    // fertig. Ein Fehlschlag lässt die Rollen stehen; die Zuweisung ist der
    // zweite Schritt, nicht der erste.
    usersApi
      .list({ limit: 100 })
      .then((r) => setBenutzer(r.data.data))
      .catch(() => undefined)
  }, [])

  /**
   * Hängt eine Rolle an einen Benutzer oder nimmt sie ihm.
   *
   * **Additiv, nicht ersetzend** — das ist der Unterschied zur Rollenauswahl in
   * der Benutzerliste: Dort steht „die Rolle" und ersetzt alles, hier steht
   * „trägt auch diese". Genau dafür gibt es Mehrfachrollen.
   */
  const zuweisen = async (rolle: RoleView, userId: string, anhaken: boolean) => {
    setFehler(null)
    setHinweis(null)
    try {
      if (anhaken) await rolesApi.assign(rolle.id, userId)
      else await rolesApi.unassign(rolle.id, userId)
      await laden()
    } catch (e: unknown) {
      // Der häufigste Fall ist die Letzter-Administrator-Regel. Sie wird
      // serverseitig geprüft, und ihre Meldung ist die Antwort.
      setFehler(apiErrorText(e, t('roles.assignFailed', { defaultValue: 'Zuweisung nicht möglich.' })))
    }
  }

  const nameVon = (u: User) =>
    [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email

  /**
   * Die eigenen **vergebenen** Rechte — die Obergrenze für alles, was diese
   * Seite anbieten darf.
   *
   * „Niemand vergibt mehr, als er selbst hat" steht im Backend und wird dort
   * durchgesetzt; hier verhindert dieselbe Regel, dass jemand ein Häkchen setzt,
   * das beim Speichern abgelehnt wird.
   */
  const eigeneRechte = useMemo(() => new Set(user?.permissions ?? []), [user?.permissions])

  /** Die Häkchenliste, gruppiert je Modul — aus der Registry, nicht aus einer zweiten Liste. */
  const gruppen = useMemo(
    () =>
      modulesWithPermissions().map((m) => ({
        key: m.key,
        /** Ein Baustein steht unter seiner Gruppe; das sagt der Name. */
        label: t(`modules.names.${m.key}`, { defaultValue: m.key }),
        /** Gilt der Eintrag für diesen Mandanten? `CORE` immer. */
        aktiv: m.alwaysActive === true || hasModule(m.key),
        rechte: m.permissions.filter((p) => !isFieldPermission(p)),
        felder: m.permissions.filter(isFieldPermission),
      })),
    [hasModule, t],
  )

  const beginneNeu = () => {
    setHinweis(null)
    setBearbeitet('neu')
    const leer = { key: '', name: '', description: '', permissions: new Set<string>() }
    setEntwurf(leer)
    setUrsprung(alsText(leer))
  }

  const beginneBearbeiten = (rolle: RoleView) => {
    setHinweis(null)
    setBearbeitet(rolle.id)
    const start = {
      key: rolle.key,
      name: rolle.name,
      description: rolle.description ?? '',
      permissions: new Set(rolle.permissions),
    }
    setEntwurf(start)
    setUrsprung(alsText(start))
  }

  const kopieren = async (rolle: RoleView) => {
    const vorlage = rolle.templateKey
    if (!vorlage) return
    setHinweis(null)
    setFehler(null)
    // Ein freier Schlüssel: „manager" ist belegt, also „manager_kopie", dann
    // „manager_kopie_2". Ein Schlüssel ist stabil und taucht in Protokollen auf
    // — er wird nicht aus dem Namen geraten, den der Kunde gleich ändert.
    const belegt = new Set(rollen.map((r) => r.key))
    let key = `${vorlage}_kopie`
    for (let i = 2; belegt.has(key); i++) key = `${vorlage}_kopie_${i}`

    try {
      await rolesApi.copyTemplate({
        templateKey: vorlage,
        key,
        name: t('roles.copyOf', { defaultValue: 'Kopie von {{name}}', name: rolle.name }),
      })
      await laden()
      setHinweis(t('roles.copied', { defaultValue: 'Kopie angelegt. Jetzt lässt sie sich anpassen.' }))
    } catch (e: unknown) {
      setFehler(apiErrorText(e, t('roles.copyFailed', { defaultValue: 'Die Kopie konnte nicht angelegt werden.' })))
    }
  }

  const speichern = async () => {
    setSpeichert(true)
    setFehler(null)
    setHinweis(null)
    try {
      const rechte = [...entwurf.permissions]
      if (bearbeitet === 'neu') {
        await rolesApi.create({
          key: entwurf.key,
          name: entwurf.name,
          description: entwurf.description || undefined,
          permissions: rechte,
        })
      } else if (bearbeitet) {
        await rolesApi.update(bearbeitet, {
          name: entwurf.name,
          description: entwurf.description,
          permissions: rechte,
        })
      }
      setBearbeitet(null)
      await laden()
      setHinweis(t('roles.saved', { defaultValue: 'Gespeichert. Die Änderung wirkt sofort.' }))
    } catch (e: unknown) {
      setFehler(apiErrorText(e, t('roles.saveFailed', { defaultValue: 'Speichern nicht möglich.' })))
    } finally {
      setSpeichert(false)
    }
  }

  const loeschen = async (rolle: RoleView) => {
    setFehler(null)
    setHinweis(null)
    try {
      await rolesApi.remove(rolle.id)
      await laden()
    } catch (e: unknown) {
      // Der häufigste Fall ist die Letzter-Administrator-Regel. Sie wird
      // serverseitig geprüft, und ihre Meldung ist die Antwort — sie hier
      // nachzubauen hieße, sie zweimal zu pflegen.
      setFehler(apiErrorText(e, t('roles.deleteFailed', { defaultValue: 'Löschen nicht möglich.' })))
    }
  }

  const umschalten = (permission: string) => {
    setEntwurf((prev) => {
      const next = new Set(prev.permissions)
      if (next.has(permission)) next.delete(permission)
      else next.add(permission)
      return { ...prev, permissions: next }
    })
  }

  if (loading) {
    return (
      <div className={pageWide}>
        <h1 className={pageTitle}>{t('roles.title', { defaultValue: 'Rollen & Rechte' })}</h1>
        <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Lädt…' })}</p>
      </div>
    )
  }

  return (
    <div className={pageWide}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className={pageTitle}>{t('roles.title', { defaultValue: 'Rollen & Rechte' })}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            {t('roles.intro', {
              defaultValue:
                'Eine Rolle ist eine Menge von Rechten. Mehrere Rollen addieren sich — wer „Lieferscheine" und „Vorarbeiter" trägt, darf die Summe. Die fünf Systemrollen sind der Einstieg: kopiere eine und passe die Kopie an.',
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={beginneNeu}
          className="inline-flex shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          {t('roles.new', { defaultValue: 'Eigene Rolle' })}
        </button>
      </div>

      {fehler && (
        <div className="rounded-md border border-red-400 bg-red-50 p-3 text-sm text-red-700 dark:border-red-600 dark:bg-red-950/40 dark:text-red-300">
          {fehler}
        </div>
      )}
      {hinweis && (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm text-foreground">
          {hinweis}
        </div>
      )}

      {/* ── Der Bearbeitungsbereich ─────────────────────────────────────── */}
      {bearbeitet && (
        <section className={formCard}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-primary">
              {bearbeitet === 'neu'
                ? t('roles.newTitle', { defaultValue: 'Neue Rolle' })
                : t('roles.editTitle', { defaultValue: 'Rolle bearbeiten' })}
            </h2>
            <button
              type="button"
              onClick={() => setBearbeitet(null)}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
              {t('common.cancel', { defaultValue: 'Abbrechen' })}
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className={fieldLabel} htmlFor="rolle-name">
                {t('roles.name', { defaultValue: 'Name' })}
              </label>
              <input
                id="rolle-name"
                className={inputBase}
                value={entwurf.name}
                onChange={(e) => setEntwurf((p) => ({ ...p, name: e.target.value }))}
                placeholder={t('roles.namePlaceholder', {
                  defaultValue: 'Schriftverkehr: Lieferscheine ohne Preise',
                })}
              />
            </div>
            {bearbeitet === 'neu' && (
              <div className="space-y-1.5">
                <label className={fieldLabel} htmlFor="rolle-key">
                  {t('roles.key', { defaultValue: 'Schlüssel' })}
                </label>
                <input
                  id="rolle-key"
                  className={inputBase}
                  value={entwurf.key}
                  onChange={(e) =>
                    setEntwurf((p) => ({ ...p, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))
                  }
                  placeholder="correspondence_lieferscheine"
                />
                <p className="text-xs text-muted-foreground">
                  {t('roles.keyHint', {
                    defaultValue:
                      'Bleibt gleich, auch wenn der Name sich ändert — er steht in Protokollen und Meldungen.',
                  })}
                </p>
              </div>
            )}
          </div>

          {/* ── Die Häkchenliste, gruppiert je Modul ─────────────────────── */}
          <div className="mt-5 space-y-4">
            {gruppen.map((gruppe) => (
              <fieldset key={gruppe.key} className="rounded-md border border-border/60 p-3">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  {gruppe.label}
                  {!gruppe.aktiv && (
                    <span className="ml-2 font-normal normal-case text-muted-foreground">
                      {t('roles.moduleNotBooked', { defaultValue: '— nicht gebucht' })}
                    </span>
                  )}
                </legend>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {[...gruppe.rechte, ...gruppe.felder].map((p) => {
                    const darfVergeben = eigeneRechte.has(p)
                    // Zwei verschiedene Gründe, warum ein Häkchen nicht geht —
                    // und beide gehören erklärt, nicht verborgen.
                    const gesperrt = !gruppe.aktiv || !darfVergeben
                    const grund = !gruppe.aktiv
                      ? t('roles.lockedNoModule', {
                          defaultValue: 'Dieses Modul ist nicht gebucht — das Recht bliebe wirkungslos.',
                        })
                      : !darfVergeben
                        ? t('roles.lockedNotOwn', {
                            defaultValue: 'Du kannst nur Rechte vergeben, die du selbst hast.',
                          })
                        : undefined
                    return (
                      <label
                        key={p}
                        title={grund}
                        className={`flex items-start gap-2 rounded px-1 py-0.5 text-sm ${
                          gesperrt ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          disabled={gesperrt}
                          checked={entwurf.permissions.has(p)}
                          onChange={() => umschalten(p)}
                        />
                        <span>
                          {rechtLabel(p)}
                          {isFieldPermission(p) && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              {t('roles.fieldRight', { defaultValue: '(Feldrecht)' })}
                            </span>
                          )}
                        </span>
                      </label>
                    )
                  })}
                </div>
              </fieldset>
            ))}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              disabled={speichert || !entwurf.name || (bearbeitet === 'neu' && !entwurf.key)}
              onClick={() => void speichern()}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {t('common.save', { defaultValue: 'Speichern' })}
            </button>
            <span className="text-xs text-muted-foreground">
              {t('roles.selectedCount', {
                defaultValue: '{{count}} Rechte gewählt',
                count: entwurf.permissions.size,
              })}
            </span>
          </div>
        </section>
      )}

      {/* ── Die Rollen ──────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {rollen.map((rolle) => (
          <article key={rolle.id} className="rounded-lg border border-primary/30 bg-background p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                  {rolle.isSystem && <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />}
                  {rolle.isSystem
                    ? t(`roles.templates.${rolle.templateKey}.name`, { defaultValue: rolle.name })
                    : rolle.name}
                  {rolle.isSystem && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                      {t('roles.systemRole', { defaultValue: 'Systemrolle' })}
                    </span>
                  )}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {rolle.isSystem
                    ? t(`roles.templates.${rolle.templateKey}.description`, {
                        defaultValue: rolle.description ?? '',
                      })
                    : rolle.description}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t('roles.stats', {
                    defaultValue: '{{rights}} Rechte · {{users}} Benutzer',
                    rights: rolle.permissions.length,
                    users: rolle.assignedCount,
                  })}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {rolle.isSystem ? (
                  <button
                    type="button"
                    onClick={() => void kopieren(rolle)}
                    title={t('roles.copyHint', {
                      defaultValue:
                        'Systemrollen bleiben unverändert — die Kopie lässt sich beliebig anpassen.',
                    })}
                    className="inline-flex items-center gap-2 rounded-md border border-primary/40 px-3 py-1.5 text-sm hover:bg-muted/40"
                  >
                    <Copy className="h-4 w-4" />
                    {t('roles.copy', { defaultValue: 'Kopieren' })}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => beginneBearbeiten(rolle)}
                      className="inline-flex items-center gap-2 rounded-md border border-primary/40 px-3 py-1.5 text-sm hover:bg-muted/40"
                    >
                      <Pencil className="h-4 w-4" />
                      {t('common.edit', { defaultValue: 'Bearbeiten' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => void loeschen(rolle)}
                      className="inline-flex items-center gap-2 rounded-md border border-red-400 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t('common.delete', { defaultValue: 'Löschen' })}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* ── Wer trägt sie ──────────────────────────────────────────── */}
            <button
              type="button"
              onClick={() => setOffeneZuweisung(offeneZuweisung === rolle.id ? null : rolle.id)}
              className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              {offeneZuweisung === rolle.id ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {t('roles.assignees', { defaultValue: 'Wer trägt diese Rolle' })}
            </button>

            {offeneZuweisung === rolle.id && (
              <div className="mt-2 rounded-md border border-border/60 p-3">
                <p className="mb-2 text-xs text-muted-foreground">
                  {t('roles.assignHint', {
                    defaultValue:
                      'Rechte addieren sich: Wer hier zusätzlich angehakt wird, behält seine übrigen Rollen.',
                  })}
                </p>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {benutzer.map((u) => {
                    const traegt = u.roles.some((r) => r.key === rolle.key)
                    return (
                      <label
                        key={u.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted/40"
                      >
                        <input
                          type="checkbox"
                          checked={traegt}
                          onChange={(e) => void zuweisen(rolle, u.id, e.target.checked)}
                        />
                        <span className={u.isActive ? '' : 'text-muted-foreground line-through'}>
                          {nameVon(u)}
                        </span>
                      </label>
                    )
                  })}
                  {benutzer.length === 0 && (
                    <span className="text-sm text-muted-foreground">
                      {t('roles.noUsers', { defaultValue: 'Keine Benutzer geladen.' })}
                    </span>
                  )}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      {/* Der Bauplan als Fußnote: Wie viele Rechte es überhaupt gibt und wie
          viele davon dieser Mandant benutzen kann. Ohne diese Zahl liest sich
          „12 Rechte" wie viel oder wie wenig — je nach Tagesform. */}
      <p className="text-xs text-muted-foreground">
        {t('roles.catalogNote', {
          defaultValue:
            'Der Katalog umfasst {{total}} Rechte aus {{modules}} Modulen. Rechte eines nicht gebuchten Moduls bleiben wirkungslos und sind deshalb ausgegraut.',
          total: MODULE_REGISTRY.reduce((n, m) => n + m.permissions.length, 0),
          modules: modulesWithPermissions().length,
        })}
        {' '}
        {t('roles.templateNote', {
          defaultValue: '{{count}} Systemvorlagen stehen bereit.',
          count: ROLE_TEMPLATES.length,
        })}
      </p>
    </div>
  )
}
